/**
 * The list of one collection's Items.
 *
 * Search and the filters run in the browser over the whole list. That is not a
 * shortcut: there are 37 Providers and 77 Articles, the API already sends a
 * summary of each, and a server round trip per keystroke would make a list of
 * 37 people feel slower than a list of 37 people has any right to.
 *
 * What is special about a collection — its extra columns and how each is
 * drawn, the Option field it can be filtered by, its own filters, the hints
 * after a name, the note under the list, whether a row can be copied, whether
 * it can be dragged into order — is read off its registry entry
 * (shared/collections.js), so this screen has no idea which collection it is
 * showing and does not need one. The filters can arrive in the address
 * (`#/faq?status=published&page=unplaced`), which is how a dashboard card
 * opens the list on exactly the rows it counted; `rowMatches` is the same
 * function the dashboard's numbers came from.
 *
 * A collection with `reorder` can be dragged into order within each group,
 * which writes the Number field named there. Ordinary HTML drag-and-drop, with
 * the up/down buttons beside it doing the same thing — dragging is not
 * reachable from a keyboard, and the clinic's list is exactly the kind of thing
 * somebody reorders while on the phone.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../api.js";
import { Message, Pill } from "../components/bits.jsx";
import { go } from "../app.jsx";
import {
  cellOf,
  collectionBySlug,
  itemUrl,
  optionName,
  optionNames,
  reorderElsewhere,
  reorderGroups,
  reorderMembers,
  rowMatches,
} from "../../shared/collections.js";

/**
 * @param {object} props
 * @param {string} props.collection
 * @param {(action: string) => boolean} props.may
 * @param {Record<string, string>} [props.query]  filters from the address —
 *   `#/faq?status=published&page=unplaced` — the way a dashboard card arrives
 */
export default function ItemList({ collection, may, query = {} }) {
  const entry = collectionBySlug(collection);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  // Every filter in one place: `status`, the Option filter under its field's
  // name, and each of the entry's own under its key — the shape `rowMatches`
  // reads, and the shape the address carries.
  const [filters, setFilters] = useState(() => ({ status: "all", ...query }));
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState(null);
  // "Copy": the Item a new one starts from, and its name for the dialog.
  const [copyOf, setCopyOf] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  // What the last Reorder save said, and the group it was of. Held here
  // because saving reloads the list, which takes the Reorder card down and
  // up again with the new order — and it should come up on the same group,
  // with the message, not on the first one.
  const [reorderNotice, setReorderNotice] = useState(null);
  const [reorderGroup, setReorderGroup] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    setError(null);
    try {
      setData(await api.get(`collections/${collection}`));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load the list.");
    }
  }, [collection]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const filter = entry.optionFilter;
  const filterNames = useMemo(() => (filter && data ? optionNames(data.schema, filter.field) : []), [filter, data]);
  const extraFilters = useMemo(
    () => (data ? entry.listFilters.map((f) => ({ ...f, options: f.choices(data) })) : []),
    [entry, data]
  );

  const shown = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.items.filter((item) => {
      if (!rowMatches(entry, item, filters, data.schema)) return false;
      if (!needle) return true;
      return `${item.name} ${item.slug}`.toLowerCase().includes(needle);
    });
  }, [data, search, filters, entry]);

  const note = data ? entry.listNote(data.items) : null;
  const cellContext = { schema: data?.schema, now: new Date() };
  const hasThumbs = entry.imageFields.length > 0;

  function startCopy(item) {
    setCopyOf(item);
    setNewName(item.name || "");
    setNewSlug(null);
    setSuggestion(null);
    setCreating(true);
  }

  async function create(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuggestion(null);
    try {
      const body = { name: newName };
      if (newSlug) body.slug = newSlug;
      if (copyOf) body.from = copyOf.id;
      const { item } = await api.post(`collections/${collection}`, body);
      go(`${collection}/${item.id}`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not start it.");
      // The address is taken; the API says which one to offer instead.
      if (problem instanceof ApiError && problem.status === 409 && problem.data?.suggestedSlug) {
        setSuggestion(problem.data.suggestedSlug);
      }
      setNewSlug(null);
      setSaving(false);
    }
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>{entry.many}</h1>
          <p className="sub">{entry.blurb}</p>
        </div>
        <button
          className="primary"
          onClick={() => {
            setCopyOf(null);
            setCreating((v) => !v);
          }}
        >
          Add {/^[aeiou]/i.test(entry.one) ? "an" : "a"} {entry.one.toLowerCase()}
        </button>
      </div>

      {creating && (
        <form className="card" onSubmit={create}>
          <div className="body row">
            <div style={{ flex: "1 1 22rem" }}>
              <label htmlFor="new-name">{entry.createLabel}</label>
              <input
                id="new-name"
                autoFocus
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setNewSlug(null);
                  setSuggestion(null);
                }}
                placeholder={entry.createPlaceholder}
                required
              />
              {copyOf && (
                <p className="help">
                  Starts as a copy of <strong>{copyOf.name}</strong> — everything but its name, address and dates.
                </p>
              )}
              <p className="help">
                {entry.urlPrefix
                  ? "This also makes the web address, which cannot be changed once the page is published (ADR-0003). You can still edit the name afterwards."
                  : "You can change the wording afterwards. It has no page of its own."}
              </p>
              {suggestion && (
                <p className="help">
                  The address is taken. Use <span className="mono">{suggestion}</span> instead?{" "}
                  <button
                    type="button"
                    className="quiet"
                    onClick={() => {
                      setNewSlug(suggestion);
                      setSuggestion(null);
                      setError(null);
                    }}
                  >
                    Use it
                  </button>
                </p>
              )}
              {newSlug && (
                <p className="help">
                  Will start at <span className="mono">{newSlug}</span>.
                </p>
              )}
            </div>
            <button className="primary" disabled={saving || !newName.trim()}>
              {saving ? "Starting…" : "Start a draft"}
            </button>
            <button type="button" className="quiet" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <Message level="error">{error}</Message>

      <div className="card">
        <header className="row">
          <input
            style={{ flex: "1 1 14rem" }}
            placeholder={`Search ${entry.many.toLowerCase()}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={`Search ${entry.many}`}
          />
          <select value={filters.status || "all"} onChange={(e) => setFilter("status", e.target.value)} style={{ width: "auto" }} aria-label="Status">
            <option value="all">Every status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          {filter && (
            <select value={filters[filter.field] || "all"} onChange={(e) => setFilter(filter.field, e.target.value)} style={{ width: "auto" }} aria-label={filter.label}>
              <option value="all">{filter.every}</option>
              {filterNames.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
          )}
          {extraFilters.map((f) => (
            <select key={f.key} value={filters[f.key] || "all"} onChange={(e) => setFilter(f.key, e.target.value)} style={{ width: "auto" }} aria-label={f.label}>
              <option value="all">{f.every}</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ))}
        </header>

        <div className="body" style={{ padding: 0 }}>
          {!data ? (
            <div className="empty">Loading…</div>
          ) : !data.items.length ? (
            <div className="empty">{entry.emptyText}</div>
          ) : !shown.length ? (
            <div className="empty">Nothing matches.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  {hasThumbs && <th style={{ width: "44px" }} />}
                  <th>{entry.listNameLabel}</th>
                  {entry.listColumns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th>Status</th>
                  {entry.urlPrefix && <th>Address</th>}
                  {entry.rowActions.length > 0 && <th />}
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => (
                  <tr key={item.id} style={{ cursor: "pointer" }} onClick={() => go(`${collection}/${item.id}`)}>
                    {hasThumbs && <td>{item.image ? <img className="thumb" src={item.image} alt="" loading="lazy" /> : <div className="thumb" />}</td>}
                    <td>
                      <strong>{item.name}</strong>
                      {item.noindex && (
                        <>
                          {" "}
                          <span className="pill noindex">off Google</span>
                        </>
                      )}
                      {item.featured && <span className="hint"> · featured</span>}
                      {(item.hints || []).map((hint) => (
                        <span key={hint} className="hint">
                          {" "}
                          · {hint}
                        </span>
                      ))}
                    </td>
                    {entry.listColumns.map((column) => (
                      <td key={column.key} className={column.kind === "date" ? "mono" : undefined}>
                        <CellText cell={cellOf(column, item, cellContext)} />
                      </td>
                    ))}
                    <td>
                      <Pill status={item.status} />
                    </td>
                    {entry.urlPrefix && <td className="mono truncate">{itemUrl(collection, item.slug)}</td>}
                    {entry.rowActions.includes("copy") && (
                      <td>
                        <button
                          className="quiet"
                          onClick={(e) => {
                            e.stopPropagation();
                            startCopy(item);
                          }}
                          aria-label={`Copy ${item.name}`}
                        >
                          Copy
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {note && (
          <div className="body" style={{ paddingTop: "0.6rem" }}>
            <p className="hint">{note}</p>
          </div>
        )}
      </div>

      {entry.reorder && may("publish") && data && (
        <Reorder
          collection={collection}
          reorder={entry.reorder}
          data={data}
          notice={reorderNotice}
          group={reorderGroup}
          onSaved={(notice, group) => {
            setReorderNotice(notice);
            setReorderGroup(group);
            load();
          }}
        />
      )}
    </>
  );
}

/** A column's cell: its text, a glyph's spoken label, and a pill if it has one. */
function CellText({ cell }) {
  return (
    <>
      {cell.label ? (
        <span title={cell.label} aria-label={cell.label}>
          {cell.text}
        </span>
      ) : (
        cell.text
      )}
      {cell.pill && (
        <>
          {cell.text ? " " : ""}
          <span className={`pill ${cell.pill.tone}`}>{cell.pill.text}</span>
        </>
      )}
    </>
  );
}

/**
 * Drag one group's Items into the order the site shows them in.
 *
 * Saved as one Number per Item (`reorder.field`), lowest first — the field the
 * site already sorts by — through `PUT /api/collections/:c/order`, which
 * writes every position in **one** commit to `main`, so the order is one
 * change, one build, and either all of it lands or none of it. (It used to be
 * one save per Item, which since the branch-per-item rule went to each Item's
 * preview branch and never reached the site at all.) The group is sent along
 * as the scope, so the server can refuse a list that strayed outside it.
 *
 * What the groups are, which rows are in one and the order they start in are
 * the registry's (`reorderGroups`, `reorderMembers`): a Provider's group is
 * a discipline, a Question's an assessment page. A row in more than one group
 * says so, because the one number it gets is its place on every page.
 */
function Reorder({ collection, reorder, data, notice = null, group: lastGroup = null, onSaved }) {
  const entry = collectionBySlug(collection);
  const groups = useMemo(() => reorderGroups(entry, data), [entry, data]);
  // The group the last save was of, when it is still one; else the first.
  const [group, setGroup] = useState(groups.some((g) => g.id === lastGroup) ? lastGroup : groups[0]?.id ?? "");
  const [order, setOrder] = useState([]);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(notice);

  useEffect(() => {
    setOrder(reorderMembers(entry, data, group));
  }, [entry, data, group]);

  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setOrder(next);
    setState(null);
  };

  async function save() {
    setBusy(true);
    setState(null);
    try {
      const result = await api.put(`collections/${collection}/order`, {
        field: reorder.field,
        scope: `${reorder.groupBy}:${group}`,
        ids: order.map((item) => item.id),
        fileSha: data.fileSha,
      });
      // The parent reloads the list, which remounts this card; the message
      // rides along so it is still there when the new order appears.
      onSaved(
        {
          level: "ok",
          text: result.changed
            ? "Saved as one change. It is on the site once the build it started finishes — a few minutes, usually."
            : "Already in that order — nothing to save.",
        },
        group
      );
    } catch (problem) {
      setState({ level: "error", text: problem instanceof ApiError ? problem.message : "Could not save the order." });
    } finally {
      setBusy(false);
    }
  }

  const emptyText = reorder.emptyText || "Nobody published in this discipline.";

  return (
    <div className="card">
      <header className="spread">
        <div>
          <h2>{reorder.title}</h2>
          <p className="help">{reorder.help}</p>
        </div>
        {groups.length > 0 && (
          <label className="row" style={{ gap: "0.4rem" }}>
            {reorder.pickLabel && <span>{reorder.pickLabel}</span>}
            <select
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              style={{ width: "auto" }}
              aria-label={reorder.pickLabel ? undefined : "Group to reorder"}
            >
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>
      <div className="body">
        {!groups.length ? (
          <p className="hint">Nothing to order on yet.</p>
        ) : !order.length ? (
          <p className="hint">{emptyText}</p>
        ) : (
          <ol style={{ paddingLeft: "1.4rem", margin: 0 }}>
            {order.map((item, index) => {
              const elsewhere = reorderElsewhere(entry, data, item, group);
              return (
                <li
                  key={item.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", String(index))}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    move(Number(e.dataTransfer.getData("text/plain")), index);
                  }}
                  style={{ padding: "0.25rem 0", cursor: "grab" }}
                >
                  <span className="row">
                    {item.name}
                    {elsewhere.length > 0 && <span className="pill warn">also on {elsewhere.join(", ")}</span>}
                    <button className="quiet" onClick={() => move(index, index - 1)} disabled={index === 0} aria-label={`Move ${item.name} up`}>
                      ↑
                    </button>
                    <button
                      className="quiet"
                      onClick={() => move(index, index + 1)}
                      disabled={index === order.length - 1}
                      aria-label={`Move ${item.name} down`}
                    >
                      ↓
                    </button>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
        {state && <Message level={state.level}>{state.text}</Message>}
        <button className="primary" style={{ marginTop: "0.8rem" }} onClick={save} disabled={busy || !order.length}>
          {busy ? "Saving…" : "Save this order"}
        </button>
      </div>
    </div>
  );
}
